<template>
  <div
    class="flex flex-col justify-between gap-8 px-12 py-24 md:bg-static-default-mid md:flex-row md:justify-end md:py-24 md:pr-20"
  >
    <RevButton
      v-if="shouldDisplayWinAllBackboxesButton"
      :disabled="(isSaving || isListingRefreshing) && !isGettingAllBackboxes"
      :loading="isGettingAllBackboxes"
      size="small"
      variant="primary"
      @click="handleGetAllBackboxes"
    >
      {{ i18n(translations.winAllBackboxesButton) }}
    </RevButton>
    <RevButton
      v-if="shouldDisplayMaximizeAllSalesButton"
      :disabled="(isSaving || isListingRefreshing) && !isMaximizingAllSales"
      :loading="isMaximizingAllSales"
      size="small"
      variant="secondary"
      @click="handleMaximizeAllSales"
    >
      {{ i18n(translations.maximizeAllSalesButton) }}
    </RevButton>
  </div>
  <RevTable
    id="markets-table"
    class="bg-static-default-mid"
    :collection="allMarketsWithCountryCode"
    :columns="tableColumns"
    :transparent-header="true"
  >
    <template #body-market="{ item }">
      <div class="text-static-default-mid body-1 flex items-center gap-8">
        <Flag :country-code="item.countryCode" size="m" />
        {{ i18n.country(item.countryCode) }}
      </div>
    </template>
    <template #body-minPrice="{ item }">
      <RevInputText
        :id="`min-price-${item.countryCode}-${listing.id}`"
        v-model="minimumPricesValue[item.countryCode]"
        class="max-w-[130px]"
        :disabled="isMarketDisabled()"
        :error="getMinimumPriceErrorLabel(item.countryCode)"
        :label="
          i18n(translations.minimumPriceLabel, {
            currency: i18n.currencySign(item.currency),
          })
        "
        type="number"
        @keyup.enter="handleEditMarket(item.countryCode)"
      />
    </template>
    <template #body-targetPrice="{ item }">
      <RevInputText
        :id="`target-price-${item.countryCode}-${listing.id}`"
        v-model="targetPricesValue[item.countryCode]"
        class="max-w-[130px]"
        :disabled="isMarketDisabled()"
        :error="getTargetPriceErrorLabel(item.countryCode)"
        :label="
          i18n(translations.targetPriceLabel, {
            currency: i18n.currencySign(item.currency),
          })
        "
        type="number"
        @keyup.enter="handleEditMarket(item.countryCode)"
      />
    </template>
    <template #body-backBoxPrice="{ item }">
      <div id="backBoxPrice">
        <BackBoxCell :listing="props.listing" :market="item" />
      </div>
    </template>
    <template #body-salesMaximizerPrice="{ item }">
      <div id="pricingStrategy">
        <PricingStrategyCell
          v-if="item?.pricingStrategy || item?.pricingStrategyInformation"
          :listing="props.listing"
          :market="item"
        />
      </div>
    </template>
    <template #body-cta="{ item }">
      <div class="flex w-full flex-col gap-8">
        <RevButton
          v-if="item.backBoxIndicator === 'withoutBackbox'"
          :disabled="isMarketDisabled()"
          :loading="marketStates[item.countryCode]?.isGettingBackbox"
          size="small"
          variant="primary"
          @click="handleGetBackbox(item)"
        >
          {{ i18n(translations.winBackboxButton) }}
        </RevButton>

        <RevButton
          v-if="isDealsIterationEnabled && shouldDisplayUnlockDealButton(item)"
          :disabled="isMarketDisabled()"
          :icon="IconDealFilled"
          :loading="marketStates[item.countryCode]?.isUnlockingDeal"
          size="small"
          variant="secondary"
          @click="handleUnlockDeal(item.countryCode)"
        >
          {{ i18n(translations.unlockDealButton) }}
        </RevButton>

        <RevButton
          v-if="
            isSalesMaximizerPriceEnabled &&
            shouldDisplaySalesMaximizerButton(item)
          "
          :disabled="isMarketDisabled()"
          :icon="IconBoltFilled"
          :loading="marketStates[item.countryCode]?.isUpdatingPricingStrategy"
          size="small"
          variant="secondary"
          @click="handleUpdatePricingStrategy(item.countryCode)"
        >
          {{ i18n(translations.pricingStrategyUpdate) }}
        </RevButton>

        <RevButton
          v-if="canSaveMarket(item.countryCode)"
          :id="`save-market-${item.countryCode}-${listing.id}`"
          :disabled="isMarketDisabled()"
          :loading="marketStates[item.countryCode]?.isSaving"
          size="small"
          variant="secondary"
          @click="handleEditMarket(item.countryCode)"
        >
          {{ i18n(translations.savePriceButton) }}
        </RevButton>
      </div>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { type Ref, computed, ref, watch } from 'vue'

import type { Currency, MarketCountryCode } from '@backmarket/http-api'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevInputText } from '@ds/components/InputText'
import { RevTable } from '@ds/components/Table'
import type { Column } from '@ds/components/Table/Table.constant'
import { IconBoltFilled } from '@ds/icons/IconBoltFilled'
import { IconDealFilled } from '@ds/icons/IconDealFilled'

import type { ListingV2, MarketListing } from '~/api/listings/types'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'
import { PRICE_IS_TO_LOW_RULE } from '~/scopes/listings/pages/index/components/Listing/components/MarketList/MarketList.constants'

import { useListings } from '../../../../composables/useListings'
import { useListingsTracking } from '../../../../composables/useListingsTracking'

import translations from './ExpandedListing.translations'
import BackBoxCell from './components/BackBoxCell/BackBoxCell.vue'
import PricingStrategyCell from './components/PricingStrategyCell/PricingStrategyCell.vue'
import {
  shouldDisplaySalesMaximizerButton,
  shouldDisplayUnlockDealButton,
} from './components/PricingStrategyCell/pricingStrategy.helpers'
import { useMarketPriceValidation } from './composables/useMarketPriceValidation'

const { openErrorToast } = useTheToast()

const props = defineProps<{
  listing: ListingV2
}>()

const i18n = useI18n()
const { allMarkets } = useMarketplace()

const tracking = useTracking()
const { formatListingToTrackingEvent } = useListingsTracking()

const { isFeatureEnabled } = useBusinessFeaturesStore()

const isSalesMaximizerPriceEnabled = isFeatureEnabled(
  'isSalesMaximizerPriceEnabled',
)
const isDynamicPricingUpEnabled = isFeatureEnabled(
  'isDynamicPricingUpFirstIterationEnabled',
)
const isDealsIterationEnabled = isFeatureEnabled('isDealsListingsPageEnabled')

const tableColumns: Column[] = [
  {
    key: 'market',
    label: i18n(translations.market),
  },
  {
    key: 'minPrice',
    label: i18n(translations.minPrice),
  },
  {
    key: 'targetPrice',
    label: i18n(translations.targetPrice),
  },
  {
    key: 'backBoxPrice',
    label: i18n(translations.backBoxPrice),
    description: isDynamicPricingUpEnabled
      ? i18n(translations.backBoxPriceTooltipDynamicPricingUp)
      : i18n(translations.backBoxPriceTooltip),
  },
  ...insertIf(isSalesMaximizerPriceEnabled, [
    isDealsIterationEnabled
      ? {
          key: 'salesMaximizerPrice',
          label: i18n(translations.pricingStrategy),
          description: i18n(translations.pricingStrategyTooltip),
        }
      : {
          key: 'salesMaximizerPrice',
          label: i18n(translations.salesMaximizerPrice),
          description: i18n(translations.salesMaximizerPriceTooltip),
        },
  ]),
  {
    key: 'cta',
    label: '',
  },
]

function transformMarketsWithCountryCode(
  markets: Partial<Record<MarketCountryCode, MarketListing>>,
  backBoxIndicator: 'withBackbox' | 'withoutBackbox' | 'offline',
) {
  return Object.entries(markets).map(([countryCode, marketListing]) => ({
    ...marketListing,
    countryCode: countryCode as MarketCountryCode,
    currency: allMarkets[countryCode as MarketCountryCode].defaultCurrency,
    backBoxIndicator,
  }))
}

const allMarketsWithCountryCode = computed(() => [
  ...transformMarketsWithCountryCode(
    props.listing.markets.withBackbox,
    'withBackbox',
  ),
  ...transformMarketsWithCountryCode(
    props.listing.markets.withoutBackbox,
    'withoutBackbox',
  ),
  ...transformMarketsWithCountryCode(props.listing.markets.offline, 'offline'),
])

const minimumPricesValue = ref<
  Partial<Record<MarketCountryCode, number | undefined>>
>({})
const targetPricesValue = ref<
  Partial<Record<MarketCountryCode, number | undefined>>
>({})

const {
  isTargetPriceTooHigh,
  isMinimumPriceAboveTargetPrice,
  isTargetPriceAbovePriceWhenSoldNew,
} = useMarketPriceValidation()

const {
  updateListing,
  winBackBoxForMarket,
  winAllBackboxes,
  setSalesMaximizer,
  setSalesMaximizerForAllMarkets,
  unlockDeal,
  isListingRefreshing,
} = useListings()

const isSaving = ref(false)
const isGettingAllBackboxes = ref(false)
const isMaximizingAllSales = ref(false)

// Used for displaying loading states when a request is processing - whilst a change is processing the concerned
// market CTA (such as win backbox button) is "loading", and any
// other ui for that market and other markets in the listing is "disabled" to prevent changes.
interface MarketState {
  isSaving: boolean
  isGettingBackbox: boolean
  isUpdatingPricingStrategy: boolean
  isUnlockingDeal: boolean
}

const marketStates: Ref<Partial<Record<MarketCountryCode, MarketState>>> = ref(
  {},
)

function initializeMarketStates() {
  const states: Partial<Record<MarketCountryCode, MarketState>> = {}

  allMarketsWithCountryCode.value.forEach((market) => {
    states[market.countryCode] = {
      isSaving: false,
      isGettingBackbox: false,
      isUpdatingPricingStrategy: false,
      isUnlockingDeal: false,
    }
  })

  marketStates.value = states
}

function setMarketState(
  market: MarketCountryCode,
  state: Partial<MarketState>,
) {
  if (!marketStates.value[market]) return

  marketStates.value[market] = { ...marketStates.value[market], ...state }
}

function isMarketDisabled(): boolean {
  // Check if any market is currently loading
  const anyMarketLoading = Object.values(marketStates.value).some(
    (state) =>
      state.isSaving ||
      state.isGettingBackbox ||
      state.isUpdatingPricingStrategy ||
      state.isUnlockingDeal,
  )

  return (
    isSaving.value ||
    isGettingAllBackboxes.value ||
    isMaximizingAllSales.value ||
    isListingRefreshing.value ||
    anyMarketLoading
  )
}

const canSaveMarket = computed(() => (market: MarketCountryCode) => {
  const currentMarket = allMarketsWithCountryCode.value.find(
    (mkt) => mkt.countryCode === market,
  )

  const initialTargetPriceForMarket =
    Number(currentMarket?.price?.amount) || undefined
  const initialMinimumPriceForMarket =
    Number(currentMarket?.minimumPrice?.amount) || undefined

  if (
    !targetPricesValue.value[market] ||
    Number(targetPricesValue.value[market]) === 0
  ) {
    return false
  }

  const valuesHaveChanged =
    minimumPricesValue.value[market] !== initialMinimumPriceForMarket ||
    targetPricesValue.value[market] !== initialTargetPriceForMarket

  return (
    valuesHaveChanged &&
    !isTargetPriceTooHigh(
      market,
      minimumPricesValue.value,
      targetPricesValue.value,
    ) &&
    !isMinimumPriceAboveTargetPrice(
      market,
      minimumPricesValue.value,
      targetPricesValue.value,
    ) &&
    !isTargetPriceAbovePriceWhenSoldNew(
      market,
      targetPricesValue.value,
      currentMarket?.priceWhenSoldNew?.amount
        ? Number(currentMarket.priceWhenSoldNew.amount)
        : undefined,
    )
  )
})

const shouldDisplayMaximizeAllSalesButton = computed(() => {
  if (!isSalesMaximizerPriceEnabled) return false

  const marketsWithSalesMaximizer = allMarketsWithCountryCode.value.filter(
    (market) => market.pricingStrategy?.salesMaximizerPrice,
  )

  if (marketsWithSalesMaximizer.length === 0) return false

  const hasMarketsWithOpportunities = marketsWithSalesMaximizer.some((market) =>
    shouldDisplaySalesMaximizerButton(market),
  )

  return hasMarketsWithOpportunities
})

const shouldDisplayWinAllBackboxesButton = computed(() => {
  return allMarketsWithCountryCode.value.some(
    (market) => market.backBoxIndicator === 'withoutBackbox',
  )
})

async function handleEditMarket(market: MarketCountryCode) {
  if (canSaveMarket.value(market)) {
    try {
      isSaving.value = true
      setMarketState(market, { isSaving: true })

      const marketBeingModified = allMarketsWithCountryCode.value.find(
        (item) => item.countryCode === market,
      )

      if (marketBeingModified) {
        tracking.trackClick({
          page_type: 'listings',
          zone: 'listing_price_update',
          name: 'save',
          ...formatListingToTrackingEvent(props.listing, {
            ...marketBeingModified,
            minimumPrice: {
              amount: minimumPricesValue.value[market]?.toString() ?? '',
              currency: allMarkets[market].defaultCurrency,
            },
            price: {
              amount: targetPricesValue.value[market]?.toString() ?? '',
              currency: allMarkets[market].defaultCurrency,
            },
          }),
        })
      }

      await updateListing(props.listing.id, {
        markets: {
          [market]: {
            ...insertIf(Boolean(targetPricesValue.value[market]), {
              price: {
                amount: targetPricesValue.value[market]?.toString(),
                currency: allMarkets[market].defaultCurrency,
              },
            }),

            minimumPrice: minimumPricesValue.value[market]
              ? {
                  amount: minimumPricesValue.value[market]?.toString(),
                  currency: allMarkets[market].defaultCurrency,
                }
              : null,
          },
        },
      })
    } catch (error) {
      openErrorToast({
        content: getApiErrorMessage(error as unknown as Error),
      })
    } finally {
      isSaving.value = false
      setMarketState(market, { isSaving: false })
    }
  }
}

async function handleUnlockDeal(marketCountryCode: MarketCountryCode) {
  setMarketState(marketCountryCode, { isUnlockingDeal: true })
  isSaving.value = true
  try {
    await unlockDeal(props.listing.id, marketCountryCode)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    setMarketState(marketCountryCode, { isUnlockingDeal: false })
    isSaving.value = false
  }
}

async function handleGetBackbox(
  market: MarketListing & { countryCode: MarketCountryCode },
) {
  setMarketState(market.countryCode, { isGettingBackbox: true })
  isSaving.value = true

  const marketBeingModified = allMarketsWithCountryCode.value.find(
    (item) => item.countryCode === market.countryCode,
  )

  tracking.trackClick({
    page_type: 'listings',
    zone: 'listing_price_update',
    name: 'win-backbox',
    ...formatListingToTrackingEvent(props.listing, marketBeingModified),
  })

  try {
    await winBackBoxForMarket(props.listing.id, market.countryCode)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    setMarketState(market.countryCode, { isGettingBackbox: false })
    isSaving.value = false
  }
}

async function handleUpdatePricingStrategy(
  marketCountryCode: MarketCountryCode,
) {
  setMarketState(marketCountryCode, { isUpdatingPricingStrategy: true })
  isSaving.value = true

  const marketBeingModified = allMarketsWithCountryCode.value.find(
    (item) => item.countryCode === marketCountryCode,
  )

  tracking.trackClick({
    page_type: 'listings',
    zone: 'listing_price_update',
    name: 'optimal_price',
    ...formatListingToTrackingEvent(props.listing, marketBeingModified),
  })

  try {
    await setSalesMaximizer(props.listing.id, marketCountryCode)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    setMarketState(marketCountryCode, { isUpdatingPricingStrategy: false })
    isSaving.value = false
  }
}

async function handleGetAllBackboxes() {
  try {
    isGettingAllBackboxes.value = true
    isSaving.value = true

    tracking.trackClick({
      page_type: 'listings',
      zone: 'listing_price_update',
      name: 'win-all-backbox',
      ...formatListingToTrackingEvent(props.listing),
    })

    await winAllBackboxes(props.listing.id)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isGettingAllBackboxes.value = false
    isSaving.value = false
  }
}

async function handleMaximizeAllSales() {
  try {
    isMaximizingAllSales.value = true
    isSaving.value = true

    await setSalesMaximizerForAllMarkets(props.listing.id)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isMaximizingAllSales.value = false
    isSaving.value = false
  }
}

function getTargetPriceErrorLabel(marketCountryCode: MarketCountryCode) {
  if (
    isTargetPriceTooHigh(
      marketCountryCode,
      minimumPricesValue.value,
      targetPricesValue.value,
    )
  ) {
    return i18n(translations.targetPriceIsTooHighError, {
      limit: Math.round(
        PRICE_IS_TO_LOW_RULE.MAXIMUM_TARGET_PRICE_MULTIPLIER * 100 - 100,
      ),
    })
  }

  const priceWhenSoldNew = allMarketsWithCountryCode.value.find(
    (mkt) => mkt.countryCode === marketCountryCode,
  )?.priceWhenSoldNew

  if (
    isTargetPriceAbovePriceWhenSoldNew(
      marketCountryCode,
      targetPricesValue.value,
      priceWhenSoldNew ? Number(priceWhenSoldNew.amount) : undefined,
    )
  ) {
    return i18n(translations.targetPriceExceedsPriceWhenSoldNewError, {
      newPrice: i18n.price(priceWhenSoldNew?.amount ?? 0, {
        currency: priceWhenSoldNew?.currency as Currency,
      }),
    })
  }

  return undefined
}

function getMinimumPriceErrorLabel(marketCountryCode: MarketCountryCode) {
  if (
    isMinimumPriceAboveTargetPrice(
      marketCountryCode,
      minimumPricesValue.value,
      targetPricesValue.value,
    )
  ) {
    return i18n(translations.minimumPriceAboveTargetPriceError)
  }

  return undefined
}

function initializeInputValues() {
  allMarketsWithCountryCode.value.forEach((market) => {
    minimumPricesValue.value[market.countryCode] =
      Number(market.minimumPrice?.amount) || undefined

    targetPricesValue.value[market.countryCode] =
      Number(market.price?.amount) || undefined
  })
}

watch(
  () => props.listing,
  () => {
    initializeInputValues()
    initializeMarketStates()
  },
  { immediate: true },
)
</script>

<style scoped>
#markets-table:deep(td.group) {
  @apply max-md:justify-between;
}

/* Show the column names */
#markets-table:deep(td.group):before {
  @apply max-md:block;
}

#markets-table:deep(td.group:has(#pricingStrategy)):before {
  @apply max-md:hidden;
}

#markets-table:deep(td.group:has(#backBoxPrice)):before {
  @apply max-md:hidden;
}

#markets-table:deep(td.group):empty {
  @apply max-md:hidden;
}

#markets-table:deep(td.group):last-of-type {
  @apply max-md:block;
}

/* Temporary hack to make sure the tooltips of the table headers can be read properly */
#markets-table:deep(th > div > span) {
  @apply z-[1];
}
</style>
